// apps/share-web/src/song-screen/song-screen-store.ts
//
// Состояние телефонного экрана песни. Режим (Mix / Chart & Lyrics) и «моя
// партия» переживают песню и перезагрузку — это свойства человека («у басиста и
// вокалиста разные задачи», решение Сергея 12.10). Петля и микс — свойства этой
// песни, живут до смены песни (экран монтируется заново на каждую песню).
//
// ⚠️ localStorage в приватном режиме БРОСАЕТ, а не возвращает null — поэтому
// каждое обращение в try/catch (тот же приём, что bw.lib.* в library-view.ts).
import type { TrackMix } from './mix-presets.js';

export type SongMode = 'mix' | 'chart';
export type MyPartMode = 'only' | 'without' | null;

export interface SongScreenState {
  mode: SongMode;
  loopSectionId: string | null;
  myPartName: string | null;
  myPartMode: MyPartMode;
  mix: Readonly<Record<string, TrackMix>>;
  /** Множитель Master (1 = 0 dB). */
  master: number;
}

export interface StoreStorage {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  /** Нужен, чтобы сброс «моей партии» пережил перезагрузку; без него сброс не сохранится. */
  removeItem?(k: string): void;
}

export const MODE_KEY = 'bw.song.mode';
export const MY_PART_KEY = 'bw.mypart.v1';

export interface SongScreenStore {
  get(): SongScreenState;
  set(patch: Partial<SongScreenState>): void;
  subscribe(fn: (s: SongScreenState, prev: SongScreenState) => void): () => void;
}

export interface StoreInit {
  tracks: ReadonlyArray<{ id: string; initialVolume?: number; initialMuted?: boolean; initialSoloed?: boolean }>;
  /** undefined — window.localStorage; null — без хранилища (тесты). */
  storage?: StoreStorage | null;
}

function defaultStorage(): StoreStorage | null {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}
function read(st: StoreStorage | null, k: string): string | null {
  try { return st ? st.getItem(k) : null; } catch { return null; }
}
function write(st: StoreStorage | null, k: string, v: string): void {
  try { st?.setItem(k, v); } catch { /* приватный режим: настройка просто не запомнится */ }
}
function remove(st: StoreStorage | null, k: string): void {
  try { st?.removeItem?.(k); } catch { /* приватный режим: сброс просто не запомнится */ }
}

export function createSongScreenStore(init: StoreInit): SongScreenStore {
  const st = init.storage === undefined ? defaultStorage() : init.storage;
  const mix: Record<string, TrackMix> = {};
  for (const t of init.tracks) {
    mix[t.id] = { gain: t.initialVolume ?? 1, muted: !!t.initialMuted, soloed: !!t.initialSoloed };
  }
  let state: SongScreenState = {
    mode: read(st, MODE_KEY) === 'chart' ? 'chart' : 'mix',
    loopSectionId: null,
    myPartName: read(st, MY_PART_KEY),
    myPartMode: null,
    mix,
    master: 1,
  };
  const subs = new Set<(s: SongScreenState, prev: SongScreenState) => void>();
  return {
    get: () => state,
    // ⚠️ Подписчик не должен звать set() синхронно из своего колбэка: вложенный
    // set разошлёт своё (state, prev) раньше, чем доиграет внешняя рассылка, и
    // остальные подписчики увидят пары в обратном порядке. Нужна реакция на
    // реакцию — отложите её (queueMicrotask / rAF).
    set(patch) {
      const prev = state;
      const keys = Object.keys(patch) as Array<keyof SongScreenState>;
      if (keys.every((k) => Object.is(patch[k], prev[k]))) return; // пустой патч не будит экран
      const next: SongScreenState = { ...prev, ...patch };
      state = next;
      if (patch.mode && patch.mode !== prev.mode) write(st, MODE_KEY, patch.mode);
      if ('myPartName' in patch && patch.myPartName !== prev.myPartName) {
        if (patch.myPartName) write(st, MY_PART_KEY, patch.myPartName);
        else remove(st, MY_PART_KEY);
      }
      // Рассылка по снимку: отписанный по ходу дела не вызывается; бросивший не
      // останавливает остальных, а первая ошибка всплывает ПОСЛЕ рассылки (баг
      // виден в тестах и консоли, но каждый подписчик успел отработать).
      let failed = false;
      let firstError: unknown;
      for (const fn of [...subs]) {
        if (!subs.has(fn)) continue;
        try { fn(next, prev); } catch (e) { if (!failed) { failed = true; firstError = e; } }
      }
      if (failed) throw firstError;
    },
    subscribe(fn) { subs.add(fn); return () => { subs.delete(fn); }; },
  };
}
